import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingDown, CheckCircle, ArrowRight, Award, Zap, DollarSign, BarChart3, PieChart, Settings, Target } from 'lucide-react';

export default function FinOpsCostOptimization() {
  const benefits = [
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "30-40% Cost Reduction",
      description: "Proven track record of reducing cloud spend through intelligent optimization"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Cost Visibility",
      description: "Complete visibility into cloud spending with detailed attribution and forecasting"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Right-Sizing",
      description: "Automated resource optimization matching workload requirements"
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: "Automated Governance",
      description: "Policy-driven controls preventing cost overruns and waste"
    }
  ];

  const optimizationAreas = [
    {
      area: "Compute Optimization",
      savings: "35-45%",
      strategies: [
        "Right-sizing EC2/VM instances",
        "Reserved instances and savings plans",
        "Spot instances for batch workloads",
        "Auto-scaling policies",
        "Graviton/ARM migration"
      ]
    },
    {
      area: "Storage Optimization",
      savings: "40-50%",
      strategies: [
        "S3 intelligent tiering",
        "EBS volume optimization",
        "Snapshot lifecycle policies",
        "Archive and cold storage",
        "Unused volume cleanup"
      ]
    },
    {
      area: "Database Optimization",
      savings: "30-40%",
      strategies: [
        "RDS reserved instances",
        "Aurora Serverless v2",
        "Read replica optimization",
        "Connection pooling",
        "Query optimization"
      ]
    },
    {
      area: "Network Optimization",
      savings: "20-30%",
      strategies: [
        "Data transfer optimization",
        "VPC endpoint usage",
        "CloudFront optimization",
        "Direct Connect vs VPN",
        "Inter-AZ traffic reduction"
      ]
    }
  ];

  const tools = [
    { name: "AWS Cost Explorer", use: "Cost analysis and forecasting" },
    { name: "CloudHealth", use: "Multi-cloud cost management" },
    { name: "Kubecost", use: "Kubernetes cost attribution" },
    { name: "Infracost", use: "IaC cost estimation" },
    { name: "CloudWatch", use: "Resource utilization monitoring" },
    { name: "Custom Dashboards", use: "Executive reporting" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&auto=format&fit=crop&q=80"
            alt="FinOps Cost Optimization"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-indigo-900/90 to-purple-900/95"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white mb-6">
              <TrendingDown className="w-4 h-4 text-green-400" />
              <span>Cloud Cost Optimization</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              FinOps & Cost Optimization
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Reduce cloud spending by 30-40% through intelligent resource management,
              right-sizing, and automated governance policies.
            </p>

            <div className="flex flex-wrap gap-4">
              <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-8 py-4 rounded-full font-bold transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
                Schedule Consultation <ArrowRight className="w-5 h-5" />
              </a>
              <Link to="/#case-studies"
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold transition-all inline-flex items-center gap-2">
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Maximize Cloud ROI</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform cloud spending from a cost center to a strategic advantage
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center text-white mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Optimization Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Cost Optimization Strategies</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive approach covering all major cloud cost areas
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {optimizationAreas.map((area, idx) => (
              <div key={idx} className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 hover:shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-purple-600">{area.area}</h3>
                  <div className="bg-green-100 text-green-700 px-4 py-2 rounded-full font-bold text-sm">
                    {area.savings} savings
                  </div>
                </div>
                <ul className="space-y-3">
                  {area.strategies.map((strategy, strategyIdx) => (
                    <li key={strategyIdx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{strategy}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service Description */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop&q=80"
                alt="Cost Analytics"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-2xl max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="w-8 h-8 text-purple-600" />
                  <div className="text-3xl font-bold text-purple-600">$5M+</div>
                </div>
                <div className="text-sm text-gray-600">Total Savings Delivered</div>
              </div>
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Proven Cost Optimization Expertise
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                I've helped companies save over $5M in cloud costs through systematic optimization. With deep expertise in AWS and Azure cost management, I deliver measurable ROI within the first month.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Cost Analysis & Reporting</h4>
                    <p className="text-gray-600">Detailed cost attribution by service, team, project, and environment</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Reserved Instance Planning</h4>
                    <p className="text-gray-600">Optimal RI/SP coverage analysis and procurement strategy</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Automated Policies</h4>
                    <p className="text-gray-600">Budget alerts, auto-shutdown, and resource tagging enforcement</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">FinOps Culture</h4>
                    <p className="text-gray-600">Team training and cost-aware development practices</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">FinOps Tools & Platforms</h2>
            <p className="text-xl text-gray-600">
              Best-in-class tools for cost management and optimization
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool, idx) => (
              <div key={idx} className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 hover:shadow-lg transition-all">
                <h3 className="font-bold text-lg mb-2 text-purple-600">{tool.name}</h3>
                <p className="text-gray-700 text-sm">{tool.use}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Award className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-4xl font-bold mb-4">Success Story</h2>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20">
            <h3 className="text-3xl font-bold mb-4">SaaS Platform Cost Transformation</h3>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  Challenge
                </h4>
                <p className="text-purple-100">$1.2M annual AWS spend with no cost visibility, uncontrolled resource growth, and 40% waste</p>
              </div>
              <div>
                <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  Solution
                </h4>
                <p className="text-purple-100">Implemented comprehensive FinOps program with automated policies, right-sizing, and RI optimization</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { metric: "Annual Savings", value: "$450K", improvement: "37.5% reduction" },
                { metric: "ROI", value: "15x", improvement: "First year" },
                { metric: "Cost Visibility", value: "100%", improvement: "By service & team" },
                { metric: "Optimization Time", value: "3 months", improvement: "Full implementation" }
              ].map((result, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-sm text-purple-200 mb-2">{result.metric}</div>
                  <div className="text-3xl font-bold mb-1">{result.value}</div>
                  <div className="text-xs text-green-300">{result.improvement}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Reduce Your Cloud Costs?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's analyze your cloud spending and identify opportunities for immediate savings
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-10 py-5 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
              Schedule Free Consultation <ArrowRight className="w-6 h-6" />
            </a>
            <Link to="/"
              className="bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 text-white px-10 py-5 rounded-full font-bold text-lg transition-all inline-flex items-center gap-2">
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
